import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { toast } from 'sonner';
import { 
  Loader2, CheckCircle2, XCircle, Rocket, Eye, TrendingUp, Copy, 
  Link2, RefreshCw, ExternalLink, Trash2, List, Target, ShoppingCart,
  BarChart3, Settings2, Zap, Globe, Search, Video, Key, Plus
} from 'lucide-react';
import api from '../../utils/api';

function AdminMarketingPage() {
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [directSyncing, setDirectSyncing] = useState(false);
  const [savedCampaigns, setSavedCampaigns] = useState([]);
  const [campaignResult, setCampaignResult] = useState(null);
  
  // Google Ads API State
  const [googleApiConfigured, setGoogleApiConfigured] = useState(false);
  const [testingGoogleApi, setTestingGoogleApi] = useState(false);
  const [googleApiStatus, setGoogleApiStatus] = useState(null);
  const [creatingGoogleCampaign, setCreatingGoogleCampaign] = useState(false);
  const [googleCampaigns, setGoogleCampaigns] = useState([]);
  const [googleCampaignForm, setGoogleCampaignForm] = useState({
    campaign_name: '',
    daily_budget: '500',
    bidding_strategy: 'MAXIMIZE_CONVERSIONS',
    target_roas: '',
    headlines: '',
    descriptions: '',
    final_url: 'https://tiffinandbag.com',
  });
  const [googleApiCredentials, setGoogleApiCredentials] = useState({
    client_id: '',
    client_secret: '',
    refresh_token: '',
    developer_token: '',
    customer_id: '',
    merchant_center_id: '',
  });
  
  // Google Ads state
  const [googleSettings, setGoogleSettings] = useState({
    conversion_id: '',
    conversion_label: '',
    merchant_id: '',
    google_ads_customer_id: '',
  });
  
  const [metaSettings, setMetaSettings] = useState({
    pixel_id: '',
    app_id: '',
    app_secret: '',
    access_token: '',
    ad_account_id: '',
    page_id: '',
    catalog_id: '',
  });

  const [campaignForm, setCampaignForm] = useState({
    campaign_name: '',
    daily_budget: '500',
    target_age_min: '25',
    target_age_max: '45',
    campaign_type: 'prospecting', // 'prospecting' or 'retargeting'
  });

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchSettings();
    fetchCampaigns();
    fetchGoogleApiCredentials();
    fetchGoogleCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGoogleApiCredentials = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/google-ads/credentials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.configured) {
        setGoogleApiConfigured(true);
        setGoogleApiCredentials(prev => ({
          ...prev,
          client_id: data.credentials.client_id || '',
          developer_token: data.credentials.developer_token || '',
          customer_id: data.credentials.customer_id || '',
          merchant_center_id: data.credentials.merchant_center_id || '',
        }));
      }
    } catch (error) {
      console.error('Failed to fetch Google Ads credentials:', error);
    }
  };

  const fetchGoogleCampaigns = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/google-ads/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setGoogleCampaigns(data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch Google campaigns:', error);
    }
  };

  const saveGoogleApiCredentials = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/google-ads/credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(googleApiCredentials),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Google Ads API credentials saved!');
        setGoogleApiConfigured(true);
      } else {
        toast.error(data.detail || 'Failed to save credentials');
      }
    } catch (error) {
      toast.error('Failed to save credentials');
    } finally {
      setLoading(false);
    }
  };

  const testGoogleApiConnection = async () => {
    setTestingGoogleApi(true);
    setGoogleApiStatus(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/google-ads/test-connection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setGoogleApiStatus(data);
      if (data.success) {
        toast.success(`Connected to ${data.account_name || 'Google Ads'}`);
      } else {
        toast.error(data.message || 'Connection failed');
      }
    } catch (error) {
      setGoogleApiStatus({ success: false, message: 'Connection failed' });
      toast.error('Connection test failed');
    } finally {
      setTestingGoogleApi(false);
    }
  };

  const createGoogleCampaign = async () => {
    if (!googleCampaignForm.campaign_name) {
      toast.error('Please enter a campaign name');
      return;
    }
    
    setCreatingGoogleCampaign(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/google-ads/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          campaign_name: googleCampaignForm.campaign_name,
          daily_budget: parseFloat(googleCampaignForm.daily_budget),
          bidding_strategy: googleCampaignForm.bidding_strategy,
          target_roas: googleCampaignForm.target_roas ? parseFloat(googleCampaignForm.target_roas) : null,
          headlines: googleCampaignForm.headlines.split('\n').filter(h => h.trim()),
          descriptions: googleCampaignForm.descriptions.split('\n').filter(d => d.trim()),
          final_url: googleCampaignForm.final_url,
        }),
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Performance Max campaign created!');
        setGoogleCampaignForm({
          campaign_name: '',
          daily_budget: '500',
          bidding_strategy: 'MAXIMIZE_CONVERSIONS',
          target_roas: '',
          headlines: '',
          descriptions: '',
          final_url: 'https://tiffinandbag.com',
        });
        fetchGoogleCampaigns();
      } else {
        toast.error(data.detail || 'Campaign creation failed');
      }
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setCreatingGoogleCampaign(false);
    }
  };

  const deleteGoogleCampaignRecord = async (campaignId) => {
    if (!window.confirm('Delete this campaign record?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/google-ads/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Campaign record deleted');
      fetchGoogleCampaigns();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const fetchSettings = async () => {
    try {
      const data = await api.adminGetMarketingSettings(token);
      if (data) {
        setMetaSettings(prev => ({ ...prev, ...data }));
        setGoogleSettings(prev => ({
          ...prev,
          conversion_id: data.google_conversion_id || '',
          conversion_label: data.google_conversion_label || '',
          merchant_id: data.google_merchant_id || '',
          google_ads_customer_id: data.google_ads_customer_id || '',
        }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSavedCampaigns(data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const saveMetaSettings = async () => {
    setLoading(true);
    try {
      await api.adminSaveMarketingSettings({ ...metaSettings, ...googleSettings }, token);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const saveGoogleSettings = async () => {
    setLoading(true);
    try {
      await api.adminSaveMarketingSettings({
        ...metaSettings,
        google_conversion_id: googleSettings.conversion_id,
        google_conversion_label: googleSettings.conversion_label,
        google_merchant_id: googleSettings.merchant_id,
        google_ads_customer_id: googleSettings.google_ads_customer_id,
      }, token);
      toast.success('Google Ads settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const testMetaConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/meta/test-connection`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          access_token: metaSettings.access_token,
          ad_account_id: metaSettings.ad_account_id,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setConnectionStatus({ success: true, message: `Connected: ${data.account_name}` });
        toast.success('Connection successful!');
      } else {
        setConnectionStatus({ success: false, message: data.detail });
        toast.error(data.detail);
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: 'Connection failed' });
      toast.error('Connection failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const syncCatalog = async () => {
    setDirectSyncing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/meta/direct-catalog-sync`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          catalog_id: metaSettings.catalog_id,
          access_token: metaSettings.access_token,
        }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(
          `✅ Synced ${data.synced_count} products!\n` +
          `📦 In Stock: ${data.in_stock_count}\n` +
          `❌ Out of Stock: ${data.out_of_stock_count}`
        );
      } else {
        toast.error(data.detail || 'Sync failed');
      }
    } catch (error) {
      toast.error('Catalog sync failed');
    } finally {
      setDirectSyncing(false);
    }
  };

  const createMetaCampaign = async () => {
    if (!campaignForm.campaign_name) {
      toast.error('Please enter a campaign name');
      return;
    }
    
    setLoading(true);
    setCampaignResult(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/meta/create-campaign`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          name: campaignForm.campaign_name,
          daily_budget: campaignForm.daily_budget,
          age_min: campaignForm.target_age_min,
          age_max: campaignForm.target_age_max,
          campaign_type: campaignForm.campaign_type,
        }),
      });
      const data = await response.json();
      
      if (data.success) {
        setCampaignResult(data);
        toast.success(`${data.campaign_type === 'retargeting' ? 'Retargeting' : 'Prospecting'} campaign created!`);
        
        // Save campaign
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/save`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` 
          },
          body: JSON.stringify({
            campaign_id: data.campaign_id,
            adset_id: data.adset_id,
            name: campaignForm.campaign_name,
            budget: campaignForm.daily_budget,
            campaign_type: data.campaign_type,
            ads_manager_url: data.ads_manager_url,
          }),
        });
        
        fetchCampaigns();
      } else {
        toast.error(data.detail || 'Campaign creation failed');
      }
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign from saved list?')) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Marketing & Ads</h1>
          <p className="text-muted-foreground">
            Manage your advertising campaigns on Meta (Facebook/Instagram) and Google
          </p>
        </div>

        <Tabs defaultValue="meta" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="meta" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Meta Ads
            </TabsTrigger>
            <TabsTrigger value="google" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Google Ads
            </TabsTrigger>
          </TabsList>

          {/* ==================== META ADS TAB ==================== */}
          <TabsContent value="meta" className="space-y-6">
            {/* Meta Pixel Setup */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold">Facebook Pixel</h2>
              </div>
              
              <div className="grid gap-4">
                <div>
                  <Label>Pixel ID</Label>
                  <Input
                    value={metaSettings.pixel_id}
                    onChange={(e) => setMetaSettings({ ...metaSettings, pixel_id: e.target.value })}
                    placeholder="123456789012345"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Find in Facebook Events Manager → Data Sources → Pixels
                  </p>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Auto-Tracked Events</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
                    <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> PageView</div>
                    <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> ViewContent</div>
                    <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> AddToCart</div>
                    <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> InitiateCheckout</div>
                    <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Purchase</div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Meta API Credentials */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Settings2 className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold">Meta Marketing API</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Ad Account ID</Label>
                  <Input
                    value={metaSettings.ad_account_id}
                    onChange={(e) => setMetaSettings({ ...metaSettings, ad_account_id: e.target.value })}
                    placeholder="123456789"
                  />
                </div>
                <div>
                  <Label>Page ID</Label>
                  <Input
                    value={metaSettings.page_id}
                    onChange={(e) => setMetaSettings({ ...metaSettings, page_id: e.target.value })}
                    placeholder="123456789"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Access Token (Long-Lived)</Label>
                  <Textarea
                    value={metaSettings.access_token}
                    onChange={(e) => setMetaSettings({ ...metaSettings, access_token: e.target.value })}
                    placeholder="EAAA..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Catalog ID</Label>
                  <Input
                    value={metaSettings.catalog_id}
                    onChange={(e) => setMetaSettings({ ...metaSettings, catalog_id: e.target.value })}
                    placeholder="123456789"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button onClick={saveMetaSettings} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Settings
                </Button>
                <Button variant="outline" onClick={testMetaConnection} disabled={testingConnection}>
                  {testingConnection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Test Connection
                </Button>
              </div>
              
              {connectionStatus && (
                <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${connectionStatus.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {connectionStatus.success ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {connectionStatus.message}
                </div>
              )}
            </Card>

            {/* Product Catalog Sync */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <RefreshCw className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold">Product Catalog</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Product Feed URL (CSV)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value="https://tiffinandbag.com/api/products/feed.csv" readOnly />
                    <Button variant="outline" onClick={() => copyToClipboard('https://tiffinandbag.com/api/products/feed.csv')}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <Button onClick={syncCatalog} disabled={directSyncing || !metaSettings.catalog_id}>
                  {directSyncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Sync Products to Facebook
                </Button>
              </div>
            </Card>

            {/* Campaign Creation */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Rocket className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold">Create Campaign</h2>
              </div>
              
              {/* Campaign Type Selection */}
              <div className="mb-4">
                <Label className="mb-2 block">Campaign Type</Label>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${campaignForm.campaign_type === 'prospecting' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="campaign_type"
                      value="prospecting"
                      checked={campaignForm.campaign_type === 'prospecting'}
                      onChange={(e) => setCampaignForm({ ...campaignForm, campaign_type: e.target.value })}
                      className="hidden"
                    />
                    <div>
                      <p className="font-medium">🎯 Prospecting (New Customers)</p>
                      <p className="text-xs text-muted-foreground">Target parents interested in school/kids products</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${campaignForm.campaign_type === 'retargeting' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="campaign_type"
                      value="retargeting"
                      checked={campaignForm.campaign_type === 'retargeting'}
                      onChange={(e) => setCampaignForm({ ...campaignForm, campaign_type: e.target.value })}
                      className="hidden"
                    />
                    <div>
                      <p className="font-medium">🔄 Retargeting (Website Visitors)</p>
                      <p className="text-xs text-muted-foreground">Target users who visited but didn't purchase</p>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Campaign Name</Label>
                  <Input
                    value={campaignForm.campaign_name}
                    onChange={(e) => setCampaignForm({ ...campaignForm, campaign_name: e.target.value })}
                    placeholder="Summer Sale Campaign"
                  />
                </div>
                <div>
                  <Label>Daily Budget (₹)</Label>
                  <Input
                    type="number"
                    value={campaignForm.daily_budget}
                    onChange={(e) => setCampaignForm({ ...campaignForm, daily_budget: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Target Age Min</Label>
                  <Input
                    type="number"
                    value={campaignForm.target_age_min}
                    onChange={(e) => setCampaignForm({ ...campaignForm, target_age_min: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Target Age Max</Label>
                  <Input
                    type="number"
                    value={campaignForm.target_age_max}
                    onChange={(e) => setCampaignForm({ ...campaignForm, target_age_max: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Targeting Info */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium text-gray-700 mb-1">
                  {campaignForm.campaign_type === 'prospecting' ? '🎯 Prospecting Targeting:' : '🔄 Retargeting Targeting:'}
                </p>
                <p className="text-gray-600">
                  {campaignForm.campaign_type === 'prospecting' 
                    ? 'India • Ages 25-45 • Optimized delivery across Facebook & Instagram'
                    : 'India • Website visitors who viewed products but didn\'t complete purchase (via Pixel)'}
                </p>
              </div>
              
              <Button onClick={createMetaCampaign} disabled={loading} className="mt-4">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
                Create {campaignForm.campaign_type === 'retargeting' ? 'Retargeting' : 'Prospecting'} Campaign
              </Button>
              
              {campaignResult && (
                <div className="mt-4 space-y-4">
                  <div className={`p-4 rounded-lg ${campaignResult.partial ? 'bg-yellow-50' : 'bg-green-50'}`}>
                    <p className={`font-medium ${campaignResult.partial ? 'text-yellow-800' : 'text-green-800'}`}>
                      {campaignResult.partial ? '⚠️ Campaign Created (Partial)' : '✅ Campaign & AdSet Created!'}
                    </p>
                    <p className={`text-sm mt-1 ${campaignResult.partial ? 'text-yellow-700' : 'text-green-700'}`}>
                      {campaignResult.message}
                    </p>
                    {campaignResult.adset_error && (
                      <p className="text-sm text-yellow-600 mt-1">AdSet Error: {campaignResult.adset_error}</p>
                    )}
                    {campaignResult.ads_manager_url && (
                      <Button variant="link" className="mt-2 p-0 h-auto" onClick={() => window.open(campaignResult.ads_manager_url, '_blank')}>
                        Open in Ads Manager <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Next Steps */}
                  {campaignResult.next_steps && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="font-medium text-blue-800 mb-2">Next Steps:</p>
                      <ul className="text-sm text-blue-700 space-y-1">
                        {campaignResult.next_steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Ad Setup Guide */}
                  {campaignResult.ad_setup_guide && (
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="font-medium text-purple-800 mb-2">{campaignResult.ad_setup_guide.title}</p>
                      <ol className="text-sm text-purple-700 space-y-1 list-decimal list-inside mb-4">
                        {campaignResult.ad_setup_guide.steps.map((step, i) => (
                          <li key={i}>{step.replace(/^\d+\.\s*/, '')}</li>
                        ))}
                      </ol>
                      
                      {campaignResult.ad_setup_guide.ad_copy_suggestions && (
                        <div className="mt-4 p-3 bg-white rounded border">
                          <p className="font-medium text-purple-800 mb-2">📝 Ad Copy Suggestions (Copy & Use):</p>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="font-medium text-gray-600">Primary Text:</span>
                              <p className="text-gray-800 bg-gray-50 p-2 rounded mt-1">{campaignResult.ad_setup_guide.ad_copy_suggestions.primary_text}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-600">Headline:</span>
                              <p className="text-gray-800 bg-gray-50 p-2 rounded mt-1">{campaignResult.ad_setup_guide.ad_copy_suggestions.headline}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-600">Description:</span>
                              <p className="text-gray-800 bg-gray-50 p-2 rounded mt-1">{campaignResult.ad_setup_guide.ad_copy_suggestions.description}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Saved Campaigns */}
            {savedCampaigns.length > 0 && (
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <List className="w-5 h-5 text-blue-600" />
                  <h2 className="text-xl font-bold">Saved Campaigns</h2>
                </div>
                
                <div className="space-y-3">
                  {savedCampaigns.map((campaign) => (
                    <div key={campaign.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{campaign.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Budget: ₹{campaign.budget}/day • {new Date(campaign.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {campaign.ads_manager_url && (
                          <Button variant="outline" size="sm" onClick={() => window.open(campaign.ads_manager_url, '_blank')}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => deleteCampaign(campaign.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ==================== GOOGLE ADS TAB ==================== */}
          <TabsContent value="google" className="space-y-6">
            {/* Google Ads Conversion Tracking */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold">Google Ads Conversion Tracking</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Conversion ID</Label>
                  <Input
                    value={googleSettings.conversion_id}
                    onChange={(e) => setGoogleSettings({ ...googleSettings, conversion_id: e.target.value })}
                    placeholder="AW-123456789"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Found in Google Ads → Tools → Conversions
                  </p>
                </div>
                <div>
                  <Label>Conversion Label (Purchase)</Label>
                  <Input
                    value={googleSettings.conversion_label}
                    onChange={(e) => setGoogleSettings({ ...googleSettings, conversion_label: e.target.value })}
                    placeholder="AbCdEfGhIjK"
                  />
                </div>
                <div>
                  <Label>Google Ads Customer ID</Label>
                  <Input
                    value={googleSettings.google_ads_customer_id}
                    onChange={(e) => setGoogleSettings({ ...googleSettings, google_ads_customer_id: e.target.value })}
                    placeholder="123-456-7890"
                  />
                </div>
                <div>
                  <Label>Merchant Center ID</Label>
                  <Input
                    value={googleSettings.merchant_id}
                    onChange={(e) => setGoogleSettings({ ...googleSettings, merchant_id: e.target.value })}
                    placeholder="123456789"
                  />
                </div>
              </div>
              
              <Button onClick={saveGoogleSettings} disabled={loading} className="mt-4">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Google Settings
              </Button>
              
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">Auto-Tracked Events (when configured)</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-green-700">
                  <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> page_view</div>
                  <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> view_item</div>
                  <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> add_to_cart</div>
                  <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> begin_checkout</div>
                  <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> purchase</div>
                </div>
              </div>
            </Card>

            {/* Google Shopping Feed */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingCart className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold">Google Shopping / Merchant Center</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Product Feed URL (XML)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value="https://tiffinandbag.com/api/products/google-feed.xml" readOnly />
                    <Button variant="outline" onClick={() => copyToClipboard('https://tiffinandbag.com/api/products/google-feed.xml')}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={() => window.open('https://tiffinandbag.com/api/products/google-feed.xml', '_blank')}>
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use this URL in Google Merchant Center → Products → Feeds
                  </p>
                </div>
                
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2">Feed includes:</h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• Product ID, Title, Description</li>
                    <li>• Price in INR with free shipping</li>
                    <li>• Stock availability</li>
                    <li>• Product images and links</li>
                    <li>• Google product categories</li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* ===== Google Ads API Integration ===== */}
            <Card className="p-6 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
              <div className="flex items-center gap-2 mb-4">
                <Key className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold">Google Ads API Integration</h2>
                <Badge variant={googleApiConfigured ? "default" : "secondary"} className="ml-2">
                  {googleApiConfigured ? "Configured" : "Not Configured"}
                </Badge>
              </div>
              
              <p className="text-muted-foreground mb-4">
                Connect your Google Ads API to create Performance Max campaigns directly from this dashboard.
              </p>
              
              <Accordion type="single" collapsible defaultValue={googleApiConfigured ? "" : "credentials"}>
                <AccordionItem value="credentials" className="border rounded-lg px-4 mb-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Settings2 className="w-4 h-4 text-green-600" />
                      <span className="font-medium">API Credentials</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4">
                    <div className="grid md:grid-cols-2 gap-4 mt-2">
                      <div>
                        <Label>OAuth Client ID</Label>
                        <Input
                          value={googleApiCredentials.client_id}
                          onChange={(e) => setGoogleApiCredentials({ ...googleApiCredentials, client_id: e.target.value })}
                          placeholder="your-client-id.apps.googleusercontent.com"
                        />
                      </div>
                      <div>
                        <Label>OAuth Client Secret</Label>
                        <Input
                          type="password"
                          value={googleApiCredentials.client_secret}
                          onChange={(e) => setGoogleApiCredentials({ ...googleApiCredentials, client_secret: e.target.value })}
                          placeholder="Your client secret"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Refresh Token</Label>
                        <Input
                          type="password"
                          value={googleApiCredentials.refresh_token}
                          onChange={(e) => setGoogleApiCredentials({ ...googleApiCredentials, refresh_token: e.target.value })}
                          placeholder="Your refresh token"
                        />
                      </div>
                      <div>
                        <Label>Developer Token</Label>
                        <Input
                          value={googleApiCredentials.developer_token}
                          onChange={(e) => setGoogleApiCredentials({ ...googleApiCredentials, developer_token: e.target.value })}
                          placeholder="Your developer token"
                        />
                      </div>
                      <div>
                        <Label>Customer ID (without hyphens)</Label>
                        <Input
                          value={googleApiCredentials.customer_id}
                          onChange={(e) => setGoogleApiCredentials({ ...googleApiCredentials, customer_id: e.target.value })}
                          placeholder="1234567890"
                        />
                      </div>
                      <div>
                        <Label>Merchant Center ID (Optional)</Label>
                        <Input
                          value={googleApiCredentials.merchant_center_id}
                          onChange={(e) => setGoogleApiCredentials({ ...googleApiCredentials, merchant_center_id: e.target.value })}
                          placeholder="123456789"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button onClick={saveGoogleApiCredentials} disabled={loading}>
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Save Credentials
                      </Button>
                      <Button variant="outline" onClick={testGoogleApiConnection} disabled={testingGoogleApi}>
                        {testingGoogleApi ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Test Connection
                      </Button>
                    </div>
                    
                    {googleApiStatus && (
                      <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${googleApiStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {googleApiStatus.success ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        {googleApiStatus.message}
                      </div>
                    )}
                    
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>How to get credentials:</strong><br/>
                        1. Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer" className="underline">Google Cloud Console</a><br/>
                        2. Create a project and enable Google Ads API<br/>
                        3. Create OAuth 2.0 credentials (Desktop App)<br/>
                        4. Get refresh token using Google's generate_user_credentials script<br/>
                        5. Get Developer Token from Google Ads → Tools → API Center
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Create Campaign Form */}
              {googleApiConfigured && (
                <div className="mt-4 p-4 border rounded-lg bg-white">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Rocket className="w-4 h-4 text-green-600" />
                    Create Performance Max Campaign
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Campaign Name *</Label>
                      <Input
                        value={googleCampaignForm.campaign_name}
                        onChange={(e) => setGoogleCampaignForm({ ...googleCampaignForm, campaign_name: e.target.value })}
                        placeholder="Summer Lunchbox Sale"
                      />
                    </div>
                    <div>
                      <Label>Daily Budget (₹) *</Label>
                      <Input
                        type="number"
                        value={googleCampaignForm.daily_budget}
                        onChange={(e) => setGoogleCampaignForm({ ...googleCampaignForm, daily_budget: e.target.value })}
                        placeholder="500"
                      />
                    </div>
                    <div>
                      <Label>Bidding Strategy</Label>
                      <select
                        className="w-full h-10 rounded-md border border-input bg-background px-3"
                        value={googleCampaignForm.bidding_strategy}
                        onChange={(e) => setGoogleCampaignForm({ ...googleCampaignForm, bidding_strategy: e.target.value })}
                      >
                        <option value="MAXIMIZE_CONVERSIONS">Maximize Conversions</option>
                        <option value="TARGET_ROAS">Target ROAS</option>
                      </select>
                    </div>
                    {googleCampaignForm.bidding_strategy === 'TARGET_ROAS' && (
                      <div>
                        <Label>Target ROAS (%)</Label>
                        <Input
                          type="number"
                          value={googleCampaignForm.target_roas}
                          onChange={(e) => setGoogleCampaignForm({ ...googleCampaignForm, target_roas: e.target.value })}
                          placeholder="400"
                        />
                      </div>
                    )}
                    <div>
                      <Label>Landing Page URL</Label>
                      <Input
                        value={googleCampaignForm.final_url}
                        onChange={(e) => setGoogleCampaignForm({ ...googleCampaignForm, final_url: e.target.value })}
                        placeholder="https://tiffinandbag.com"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Headlines (one per line, max 15)</Label>
                      <Textarea
                        value={googleCampaignForm.headlines}
                        onChange={(e) => setGoogleCampaignForm({ ...googleCampaignForm, headlines: e.target.value })}
                        placeholder="Fun Lunchboxes for Kids&#10;Back to School Sale&#10;Free Shipping on All Orders"
                        rows={3}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Descriptions (one per line, max 5)</Label>
                      <Textarea
                        value={googleCampaignForm.descriptions}
                        onChange={(e) => setGoogleCampaignForm({ ...googleCampaignForm, descriptions: e.target.value })}
                        placeholder="Shop colorful lunchboxes and school bags. BPA-free, leak-proof designs.&#10;Perfect for little ones heading back to school!"
                        rows={2}
                      />
                    </div>
                  </div>
                  
                  <Button onClick={createGoogleCampaign} disabled={creatingGoogleCampaign} className="mt-4">
                    {creatingGoogleCampaign ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Creating Campaign...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Performance Max Campaign
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    Campaigns are created in PAUSED state. Review and enable them in Google Ads Manager.
                  </p>
                </div>
              )}

              {/* Google Campaigns List */}
              {googleCampaigns.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <List className="w-4 h-4" />
                    Created Campaigns
                  </h3>
                  <div className="space-y-2">
                    {googleCampaigns.map((campaign) => (
                      <div key={campaign.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div>
                          <p className="font-medium">{campaign.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Budget: ₹{campaign.budget}/day • {campaign.status} • {new Date(campaign.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`https://ads.google.com/aw/campaigns?campaignId=${campaign.google_campaign_id}`, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteGoogleCampaignRecord(campaign.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Google Ads Automation Guide */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold">Google Ads Automation</h2>
              </div>
              
              <p className="text-muted-foreground mb-4">
                Google Ads offers powerful automation features. Here's how to maximize them:
              </p>
              
              <Accordion type="single" collapsible className="space-y-2">
                <AccordionItem value="pmax" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Rocket className="w-4 h-4 text-purple-600" />
                      <span className="font-medium">Performance Max Campaigns (Recommended)</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4">
                    <p className="mb-2">Performance Max is Google's most automated campaign type:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Runs across ALL Google properties (Search, Display, YouTube, Gmail, Maps, Discover)</li>
                      <li>Uses AI to optimize bids, audiences, and creatives automatically</li>
                      <li>Requires minimal setup - just provide assets and goals</li>
                      <li>Best for e-commerce with Shopping feed connected</li>
                    </ul>
                    <div className="mt-3 p-3 bg-purple-50 rounded">
                      <p className="font-medium text-purple-800">Setup Steps:</p>
                      <ol className="list-decimal list-inside text-purple-700 mt-1">
                        <li>Link Merchant Center to Google Ads</li>
                        <li>Create Performance Max campaign</li>
                        <li>Select "Sales" as goal</li>
                        <li>Add 5+ images, 5+ headlines, 5+ descriptions</li>
                        <li>Set daily budget (start ₹500-1000)</li>
                        <li>Let it run 2 weeks before evaluating</li>
                      </ol>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="smart-bidding" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      <span className="font-medium">Smart Bidding Strategies</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4">
                    <div className="space-y-3">
                      <div className="p-3 bg-blue-50 rounded">
                        <p className="font-medium text-blue-800">Maximize Conversions</p>
                        <p className="text-blue-700">Best for starting out. Google optimizes to get as many purchases as possible within your budget.</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded">
                        <p className="font-medium text-green-800">Target ROAS (Return on Ad Spend)</p>
                        <p className="text-green-700">Set a target like 400% ROAS (₹4 revenue for every ₹1 spent). Use after you have 30+ conversions.</p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded">
                        <p className="font-medium text-orange-800">Target CPA (Cost Per Acquisition)</p>
                        <p className="text-orange-700">Set a target cost per purchase (e.g., ₹200). Good when you know your profit margins.</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="audiences" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-orange-600" />
                      <span className="font-medium">Automated Audiences</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4">
                    <ul className="space-y-2">
                      <li className="p-2 bg-muted rounded">
                        <span className="font-medium">Optimized Targeting:</span> Let Google find high-converting users beyond your defined audiences
                      </li>
                      <li className="p-2 bg-muted rounded">
                        <span className="font-medium">Similar Audiences:</span> Automatically find people similar to your converters
                      </li>
                      <li className="p-2 bg-muted rounded">
                        <span className="font-medium">In-Market Audiences:</span> Target parents shopping for kids' products
                      </li>
                      <li className="p-2 bg-muted rounded">
                        <span className="font-medium">Remarketing:</span> Automatically retarget website visitors
                      </li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="shopping" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-green-600" />
                      <span className="font-medium">Smart Shopping (via Performance Max)</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4">
                    <p className="mb-2">With your product feed connected, Google automatically:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Shows products in Google Shopping results</li>
                      <li>Displays product ads across the web</li>
                      <li>Retargets users who viewed products</li>
                      <li>Optimizes which products to show to whom</li>
                      <li>Adjusts bids based on purchase likelihood</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="scripts" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-red-600" />
                      <span className="font-medium">Advanced: Google Ads Scripts</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4">
                    <p className="mb-2">For advanced automation, use Google Ads Scripts:</p>
                    <ul className="space-y-1 list-disc list-inside mb-3">
                      <li>Auto-pause low performing ads</li>
                      <li>Adjust budgets based on day/time</li>
                      <li>Send email alerts for anomalies</li>
                      <li>Auto-generate reports</li>
                    </ul>
                    <Button variant="outline" size="sm" onClick={() => window.open('https://developers.google.com/google-ads/scripts/docs/solutions', '_blank')}>
                      View Script Templates <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>

            {/* Quick Setup Guide */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold">Quick Setup Checklist</h2>
              </div>
              
              <div className="space-y-3">
                {[
                  { step: 1, title: 'Create Google Ads Account', link: 'https://ads.google.com', done: !!googleSettings.conversion_id },
                  { step: 2, title: 'Set Up Google Merchant Center', link: 'https://merchants.google.com', done: !!googleSettings.merchant_id },
                  { step: 3, title: 'Verify Website Ownership', link: 'https://merchants.google.com', done: false },
                  { step: 4, title: 'Add Product Feed', link: null, done: true },
                  { step: 5, title: 'Link Merchant Center to Google Ads', link: 'https://merchants.google.com/mc/settings/linkedaccounts', done: false },
                  { step: 6, title: 'Create Performance Max Campaign', link: 'https://ads.google.com', done: false },
                ].map((item) => (
                  <div key={item.step} className={`flex items-center justify-between p-3 rounded-lg ${item.done ? 'bg-green-50' : 'bg-muted'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${item.done ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                        {item.done ? <CheckCircle2 className="w-4 h-4" /> : item.step}
                      </div>
                      <span className={item.done ? 'text-green-800' : ''}>{item.title}</span>
                    </div>
                    {item.link && (
                      <Button variant="ghost" size="sm" onClick={() => window.open(item.link, '_blank')}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            {/* Budget Recommendations */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold">Budget Recommendations</h2>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-bold text-lg text-blue-600">Starter</h4>
                  <p className="text-2xl font-bold mt-2">₹500-1000/day</p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• 1 Performance Max campaign</li>
                    <li>• Test phase (2-4 weeks)</li>
                    <li>• ~100-200 clicks/day</li>
                  </ul>
                </div>
                <div className="p-4 border-2 border-green-500 rounded-lg bg-green-50">
                  <h4 className="font-bold text-lg text-green-600">Growth</h4>
                  <p className="text-2xl font-bold mt-2">₹2000-5000/day</p>
                  <ul className="text-sm text-green-700 mt-2 space-y-1">
                    <li>• Scale winning campaigns</li>
                    <li>• Add Search campaigns</li>
                    <li>• ~500-1000 clicks/day</li>
                  </ul>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-bold text-lg text-purple-600">Scale</h4>
                  <p className="text-2xl font-bold mt-2">₹10,000+/day</p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• Multiple campaign types</li>
                    <li>• YouTube & Display ads</li>
                    <li>• Dedicated account manager</li>
                  </ul>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

export default AdminMarketingPage;
